import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useReportsStore } from "@/store/store";
import { useWorkspace } from "@/context/WorkspaceContext";
import { isCompanyDashboardPath } from "@/lib/reportRoutes";
import { MIN_GENERATION_DWELL_MS } from "@/lib/progressUtils";

/** Matches the progress dialog's 100%-then-go beat, so the bar doesn't jump. */
const NAVIGATE_DELAY_MS = 500;

/**
 * Take the user to the report they started, once it finishes — but only if
 * they're passively waiting for it.
 *
 * This used to live inside `GenerateDialog`, gated on `open` — so it only
 * happened if the dialog was still on screen at the exact moment the report
 * landed. Three ordinary things broke that:
 *
 *  - The dialog invites you to dismiss it ("You can safely close this window -
 *    your report will keep generating"). Dismiss it and nothing ever navigated.
 *  - The dashboard's period resolver only follows the *selected* month, and
 *    deliberately won't move someone off a report they chose. Start a run while
 *    reading an older month and no one was left to take you to the new one.
 *  - Leaving the tab for another window means the completion is observed on the
 *    way back in, long after the dialog was closed.
 *
 * So it lives in the shell instead, keyed on the awaited report rather than on
 * any piece of UI being mounted. It fires once per report and only for one the
 * user actually started — a historical report you are simply reading is never
 * disturbed.
 *
 * Navigation is gated on *passively waiting*: the progress dialog is on
 * screen, or the user is on that company's report dashboard. Someone who
 * dismissed the dialog and went to work elsewhere in the app (Virtual Buyer,
 * Workbench, Settings, another company) gets a toast instead of being pulled
 * out of what they're doing — and the dashboard resolves to the newest report
 * on its own the next time they open the company.
 */
export const useAwaitedReport = (): void => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { refreshWorkspaces } = useWorkspace();
  const awaitedReportId = useReportsStore((s) => s.awaitedReportId);
  const awaitedDialogOpen = useReportsStore((s) => s.awaitedDialogOpen);
  const awaitedClaimedAt = useReportsStore((s) => s.awaitedClaimedAt);
  const clearAwaitedReport = useReportsStore((s) => s.clearAwaitedReport);
  // Selector down to the one report we care about, so the 5s progress tick
  // doesn't re-render the shell (and with it every routed page) while a report
  // builds — the trap AITS-540 hit when this polling moved into the shell.
  const awaitedStatus = useReportsStore((s) =>
    awaitedReportId ? s.generatingReports[awaitedReportId]?.status ?? null : null,
  );
  const awaitedCompanyId = useReportsStore((s) =>
    awaitedReportId ? s.generatingReports[awaitedReportId]?.company_id ?? null : null,
  );
  const awaitedCompanyName = useReportsStore((s) =>
    awaitedReportId ? s.generatingReports[awaitedReportId]?.company_name ?? null : null,
  );

  // Guards against a second navigation if the store is written again before the
  // delayed navigate runs (the poll keeps ticking until the list settles).
  const navigatedForRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Read through a ref, and keep it out of the effect's deps. Calling it updates
  // the workspace context, which re-creates the callback — so depending on it
  // makes this effect re-run as a *result* of its own work.
  const refreshWorkspacesRef = useRef(refreshWorkspaces);
  refreshWorkspacesRef.current = refreshWorkspaces;

  useEffect(() => {
    if (awaitedReportId == null) return;

    // Nothing to do until the poll has actually seen this report land.
    if (awaitedStatus !== "completed" && awaitedStatus !== "error") return;

    if (awaitedStatus === "error") {
      // Leave the user where they are — the dashboard surfaces the failure in
      // place. Just stop waiting, so a later unrelated poll can't strand us.
      clearAwaitedReport();
      return;
    }

    if (awaitedCompanyId == null) return;
    if (navigatedForRef.current === awaitedReportId) return;
    navigatedForRef.current = awaitedReportId;

    // The company list feeds the sidebar's per-company status, and it is only
    // refetched on explicit user actions — so without this the sidebar reads
    // "in progress" for a report that finished, until a full reload. Needed on
    // both branches below: the poll's own settle detection only sees a live
    // transition, not a report that was already finished when the tab loaded.
    void refreshWorkspacesRef.current();

    const reportPath = `/reports/${awaitedCompanyId}/${awaitedReportId}`;

    // Not passively waiting → don't interrupt. Announce instead, and drop the
    // intent for good: honouring it later would move the user off whatever
    // (possibly historical) report they choose to open next.
    const passivelyWaiting =
      awaitedDialogOpen || isCompanyDashboardPath(pathname, awaitedCompanyId);
    if (!passivelyWaiting) {
      clearAwaitedReport();
      // "The", not "your": watching a build claims it (the auto-opened progress
      // dialog does this for any member's run), so the report isn't always one
      // this user started.
      toast.success(
        awaitedCompanyName
          ? `The report for ${awaitedCompanyName} is ready`
          : "The report is ready",
        {
          id: `report-ready-${awaitedReportId}`,
          // Stays until closed — a completion shouldn't evaporate while the
          // user is looking elsewhere.
          duration: Infinity,
          closeButton: true,
          action: { label: "View", onClick: () => navigate(reportPath) },
        },
      );
      return;
    }

    // Deliberately NOT cleared when this effect re-runs, only on unmount. The
    // refresh above lands while the timer is pending and re-renders the shell;
    // a cleanup tied to dep changes would cancel the navigation it just
    // scheduled, and the once-guard above would stop it being rescheduled —
    // leaving the user exactly where this hook exists to move them from.
    // (Reaching here twice with a live timer would need a second report claimed
    // and completed inside the 500ms window; still, never stack navigations.)
    if (timerRef.current) clearTimeout(timerRef.current);
    // Same pacing floor as the dialog: a run that resolved near-instantly (the
    // backend deduplicated to the month's existing report) waits out the
    // minimum dwell before the handover, so the dialog's staged bar gets to
    // finish. Runs that took their normal minutes navigate on the usual beat.
    const sinceClaim =
      awaitedClaimedAt != null ? Date.now() - awaitedClaimedAt : Infinity;
    const delay = Math.max(
      NAVIGATE_DELAY_MS,
      MIN_GENERATION_DWELL_MS - sinceClaim + NAVIGATE_DELAY_MS,
    );
    timerRef.current = setTimeout(() => {
      navigate(reportPath);
      clearAwaitedReport();
    }, delay);
  }, [
    awaitedReportId,
    awaitedStatus,
    awaitedCompanyId,
    awaitedCompanyName,
    awaitedDialogOpen,
    awaitedClaimedAt,
    pathname,
    navigate,
    clearAwaitedReport,
  ]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);
};

export default useAwaitedReport;
